import type { CONFIG_SCHEMA } from "./screenConfigSlice";
import type {
  ModuleConfig,
  ScreenTypesCollection,
  ModesCollection,
  VisualisationTypeCollection,
} from "./moduleConfig.types";

// ─── Public parameter types ───────────────────────────────────────────────────

/** Valid screen-type names (excludes the shared `config` baseline key). */
export type ScreenTypeName = keyof Omit<ScreenTypesCollection, "config">;

/** Valid mode names (excludes the shared `config` baseline key). */
export type ModeName = keyof Omit<ModesCollection, "config">;

/** Valid visualisation-type names (excludes the shared `config` baseline key). */
export type VisualisationTypeName = keyof Omit<
  VisualisationTypeCollection,
  "config"
>;

// ─── Helper ───────────────────────────────────────────────────────────────────

function resolveSubKey(
  bounds:
    | {
        min?: string | null;
        max?: string | null;
        warningMessage?: string;
        bySubKey?: Record<
          string,
          { min?: string | null; max?: string | null; warningMessage?: string }
        >;
      }
    | undefined,
  metric: string | undefined,
  response?: string | null,
):
  | { min?: string | null; max?: string | null; warningMessage?: string }
  | undefined {
  if (!bounds) return undefined;

  const { bySubKey, warningMessage, min, max } = bounds;

  if (
    metric !== null &&
    response !== null &&
    bySubKey &&
    bySubKey[`${metric}_${response}`]
  ) {
    const subEntry = bySubKey[`${metric}_${response}`];
    return {
      min: subEntry.min ?? null,
      max: subEntry.max ?? null,
      warningMessage: subEntry?.warningMessage,
    };
  }

  if (metric != null && bySubKey) {
    const subEntry = bySubKey[String(metric)];
    if (subEntry) {
      return {
        min: subEntry.min ?? null,
        max: subEntry.max ?? null,
        warningMessage: subEntry?.warningMessage,
      };
    }
    return { min: min ?? null, max: max ?? null, warningMessage };
  }

  return { min: min ?? null, max: max ?? null, warningMessage };
}

/**
 * Resolves a flat {@link CONFIG_SCHEMA} by walking the hierarchical config
 * tree and merging each layer on top of the previous one.
 *
 * Resolution order (later layers override earlier ones):
 *   1. `module.config`
 *   2. `screenTypes.config`
 *   3. `screenTypes.[screenType].config`
 *   4. `screenTypes.[screenType].modes.config`
 *   5. `screenTypes.[screenType].modes.[mode].config`
 *   6. `screenTypes.[screenType].modes.[mode].visualisationType.config`
 *   7. `screenTypes.[screenType].modes.[mode].visualisationType.[visualizationType].config`
 *
 * Any layer that does not exist in the config object is simply skipped.
 * Parameters are optional – omitting a deeper parameter stops resolution
 * at the corresponding layer.
 *
 * @example
 * const cfg = resolveModuleConfig(equityModuleConfig, "BRAND", "POINT_IN_TIME", "PROFILE");
 */
export function resolveModuleConfig(
  moduleConfig: ModuleConfig,
  screenType?: ScreenTypeName,
  mode?: ModeName,
  visualizationType?: VisualisationTypeName,
  metric?: string | number | null,
  response?: string | null,
): CONFIG_SCHEMA {
  const { config: moduleBaseConfig, screenTypes } = moduleConfig;

  // ── Layer 1: module-level config ─────────────────────────────────────────
  const moduleConfig_ = moduleBaseConfig;

  // ── Layer 2: screenTypes.[screenType].config ──────────────────────────────
  const screenTypeConfig =
    screenType && screenTypes?.[screenType]
      ? screenTypes[screenType]!.config
      : undefined;

  // ── Mode guard: if the requested mode is not in supportedModes (checked at
  // screen-type level first, then module level), fall back to defaultMode so
  // that the resolved config is always for a valid mode on this screen.
  if (mode !== undefined) {
    const moduleSupportedModes = moduleConfig_?.supportedModes as
      | string[]
      | undefined;
    const screenTypeSupportedModes = screenTypeConfig?.supportedModes as
      | string[]
      | undefined;
    if (
      !(
        screenTypeSupportedModes?.includes(mode) ||
        moduleSupportedModes?.includes(mode)
      )
    ) {
      mode = (screenTypeConfig?.defaultMode ??
        moduleConfig_?.defaultMode) as typeof mode;
    }
  }

  // ── Layer 3: screenTypes.[screenType].modes.[mode].config ─────────────────
  const modeConfig =
    screenType && mode && screenTypes?.[screenType]?.modes?.[mode]
      ? screenTypes[screenType]!.modes![mode]!.config
      : undefined;

  // ── Layer 4: screenTypes.[screenType].modes.[mode].visualisationType.[visualizationType].config
  const vizTypeConfig =
    screenType &&
    mode &&
    visualizationType &&
    screenTypes?.[screenType]?.modes?.[mode]?.visualisationType?.[
      visualizationType
    ]
      ? screenTypes[screenType]!.modes![mode]!.visualisationType![
          visualizationType
        ]!.config
      : undefined;

  const merged: CONFIG_SCHEMA = {
    ...moduleConfig_,
    ...screenTypeConfig,
    ...modeConfig,
    ...vizTypeConfig,
  };

  // ── Resolve dateBounds / postDateBounds bySubKey → flat min/max ───────────
  // `profileMetric` is the effective selection at call time.  When a `bySubKey`
  // map exists on the merged `dateBounds`, it is resolved here so that
  // downstream consumers (selectionListener, UI components) always receive a
  // fully-flat `{ min, max, warningMessage }` object — no further look-up needed.
  if (merged.dateBounds) {
    merged.dateBounds = resolveSubKey(
      merged.dateBounds as any,
      metric == null ? undefined : String(metric),
      response,
    ) as any;
  }
  if (merged.postDateBounds) {
    merged.postDateBounds = resolveSubKey(
      merged.postDateBounds as any,
      metric == null ? undefined : String(metric),
      response,
    ) as any;
  }

  return merged;
}
